import { zodResolver } from "@hookform/resolvers/zod"
import { Link, useBlocker, useNavigate, useSearch } from "@tanstack/react-router"
import {
  Activity,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Copy,
  FlaskConical,
  History,
  MessageCircle,
  MoreHorizontal,
  Plug,
  Settings2,
  Sparkles,
  Trash2,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { AgentDetail } from "@/api/generated/AgentDetail"
import { useUpdateAgent } from "@/api/queries/agents"
import { ConfirmDialog } from "@/components/app/confirm-dialog"
import { useFormSubmit } from "@/components/app/form"
import { PageContainer } from "@/components/app/page"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useAgentActions } from "@/features/studio/agent-actions"
import { ConfigureTab } from "@/features/studio/agent-editor/configure-tab"
import { IconPicker } from "@/features/studio/agent-editor/icon-picker"
import { Playground } from "@/features/studio/agent-editor/playground"
import { RevisionsTab } from "@/features/studio/agent-editor/revisions-tab"
import { SaveBar } from "@/features/studio/agent-editor/save-bar"
import { SkillsTab } from "@/features/studio/agent-editor/skills-tab"
import { ToolsTab } from "@/features/studio/agent-editor/tools-tab"
import { type AgentFormValues, agentPatch, agentSchema, agentToForm } from "@/features/studio/agent-form"
import { fire } from "@/features/studio/fire"
import { useHotkeys } from "@/hooks/use-hotkeys"
import { useMediaQuery } from "@/hooks/use-media-query"
import { cn } from "@/lib/utils"

export const AGENT_TABS = ["configure", "tools", "skills", "revisions"] as const
export type AgentTab = (typeof AGENT_TABS)[number]

/** The agent editor: one form across tabs, sticky save bar, optional live playground. */
export function AgentEditor({ agent }: { agent: AgentDetail }) {
  const { t } = useTranslation()
  const search = useSearch({ from: "/_app/studio/agents/$agentId" })
  const navigate = useNavigate({ from: "/studio/agents/$agentId" })
  const tab: AgentTab = search.tab ?? "configure"
  const tryOpen = !!search.try
  const wide = useMediaQuery("(min-width: 1280px)")
  const actions = useAgentActions()
  const update = useUpdateAgent(agent.id)
  const [note, setNote] = useState("")

  const form = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema),
    defaultValues: agentToForm(agent),
    mode: "onChange",
  })
  const { isDirty } = form.formState

  // follow external updates (restore, other tab) while there are no local edits
  // biome-ignore lint/correctness/useExhaustiveDependencies: compare by update time only
  useEffect(() => {
    if (!form.formState.isDirty) form.reset(agentToForm(agent))
  }, [agent.updated_at, agent.archived_at])

  const submit = useFormSubmit(form, async (values) => {
    const body = agentPatch(values, form.formState.dirtyFields, note)
    const saved = await update.mutateAsync(body)
    form.reset(agentToForm(saved))
    setNote("")
  })

  useHotkeys([{ combo: "mod+s", allowInInputs: true, handler: () => isDirty && void submit() }])

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => form.formState.isDirty && current.pathname !== next.pathname,
    enableBeforeUnload: () => form.formState.isDirty,
    withResolver: true,
  })

  const leaving = useRef(false)

  const setSearch = (patch: { tab?: AgentTab; try?: boolean }) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const name = useWatch({ control: form.control, name: "name" })
  const description = useWatch({ control: form.control, name: "description" })
  const icon = useWatch({ control: form.control, name: "icon" })
  const mcpCount = useWatch({ control: form.control, name: "mcp_servers" }).length
  const skillCount = useWatch({ control: form.control, name: "skill_ids" }).length
  const archived = !!agent.archived_at
  const ref = { id: agent.id, name: agent.name, archived_at: agent.archived_at }

  const playground = <Playground agent={agent} form={form} onClose={() => setSearch({ try: undefined })} />

  return (
    <FormProvider {...form}>
      <PageContainer width="full" className="max-w-[110rem] pb-4">
        <form noValidate onSubmit={submit} className="contents">
          {/* header */}
          <div className="mb-5 flex flex-col gap-4 lg:mb-6">
            <Link
              to="/studio/agents"
              className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-xs transition-colors"
            >
              <ArrowLeft className="size-3" />
              {t("studio.agents.backToList")}
            </Link>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <IconPicker
                  name={name || agent.name}
                  value={icon}
                  archived={archived}
                  onChange={(v) => form.setValue("icon", v, { shouldDirty: true })}
                />
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="truncate text-xl font-semibold tracking-tight">{name || agent.name}</h1>
                    {archived && <StatusBadge tone="warning">{t("studio.agents.archivedBadge")}</StatusBadge>}
                    {agent.allow_as_chat_agent && !archived && (
                      <StatusBadge tone="accent">
                        <MessageCircle />
                        {t("studio.agents.chatEnabled")}
                      </StatusBadge>
                    )}
                  </div>
                  <p className="text-muted-foreground line-clamp-2 max-w-2xl text-sm">
                    {description || t("studio.agents.noDescription")}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant={tryOpen ? "secondary" : "default"}
                  aria-pressed={tryOpen}
                  onClick={() => setSearch({ try: tryOpen ? undefined : true })}
                  disabled={archived}
                  data-testid="try-agent"
                >
                  <FlaskConical />
                  {t("studio.agents.try.button")}
                </Button>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => fire(actions.duplicate(ref))}
                        aria-label={t("studio.agents.actions.duplicate")}
                      />
                    }
                  >
                    <Copy />
                  </TooltipTrigger>
                  <TooltipContent>{t("studio.agents.actions.duplicate")}</TooltipContent>
                </Tooltip>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={t("studio.agents.moreActions")}
                      />
                    }
                  >
                    <MoreHorizontal />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-auto min-w-48">
                    <DropdownMenuItem
                      onClick={() => void navigate({ to: "/insights/runs", search: { agent: agent.id } })}
                    >
                      <Activity />
                      {t("studio.agents.actions.viewRuns")}
                    </DropdownMenuItem>
                    {archived ? (
                      <DropdownMenuItem onClick={() => fire(actions.unarchive(ref))}>
                        <ArchiveRestore />
                        {t("studio.agents.actions.unarchive")}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => void actions.archive(ref)}>
                        <Archive />
                        {t("studio.agents.actions.archive")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() =>
                        void actions.remove(ref, () => {
                          form.reset(form.getValues())
                          void navigate({ to: "/studio/agents" })
                        })
                      }
                    >
                      <Trash2 />
                      {t("studio.agents.actions.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          <div
            className={cn(
              "grid items-start gap-6",
              tryOpen && wide && "grid-cols-[minmax(0,1fr)_minmax(22rem,28rem)]",
            )}
          >
            <Tabs
              value={tab}
              onValueChange={(v) => setSearch({ tab: v === "configure" ? undefined : (v as AgentTab) })}
              className="@container min-w-0 gap-5"
            >
              <div className="-mx-4 overflow-x-auto border-b px-4 sm:mx-0 sm:px-0">
                <TabsList variant="line" className="h-9">
                  <TabsTrigger value="configure" className="px-2">
                    <Settings2 />
                    {t("studio.agents.tabs.configure")}
                  </TabsTrigger>
                  <TabsTrigger value="tools" className="px-2">
                    <Plug />
                    {t("studio.agents.tabs.tools")}
                    <TabCount n={mcpCount} />
                  </TabsTrigger>
                  <TabsTrigger value="skills" className="px-2">
                    <Sparkles />
                    {t("studio.agents.tabs.skills")}
                    <TabCount n={skillCount} />
                  </TabsTrigger>
                  <TabsTrigger value="revisions" className="px-2">
                    <History />
                    {t("studio.agents.tabs.revisions")}
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="configure">
                <ConfigureTab agent={agent} />
              </TabsContent>
              <TabsContent value="tools">
                <ToolsTab />
              </TabsContent>
              <TabsContent value="skills">
                <SkillsTab />
              </TabsContent>
              <TabsContent value="revisions">
                <RevisionsTab agent={agent} dirty={isDirty} onRestored={(a) => form.reset(agentToForm(a))} />
              </TabsContent>
              <SaveBar form={form} note={note} onNoteChange={setNote} onDiscard={() => form.reset()} />
            </Tabs>

            {tryOpen && wide && (
              <aside className="bg-card sticky top-4 flex h-[calc(100dvh-7rem)] min-h-[28rem] flex-col overflow-hidden rounded-xl border">
                {playground}
              </aside>
            )}
          </div>
        </form>
      </PageContainer>

      {!wide && (
        <Sheet open={tryOpen} onOpenChange={(o) => !o && setSearch({ try: undefined })}>
          <SheetContent className="gap-0 p-0 sm:max-w-lg" showCloseButton={false}>
            <SheetHeader className="sr-only">
              <SheetTitle>{t("studio.agents.try.title")}</SheetTitle>
              <SheetDescription>{t("studio.agents.try.description")}</SheetDescription>
            </SheetHeader>
            {playground}
          </SheetContent>
        </Sheet>
      )}

      <ConfirmDialog
        open={blocker.status === "blocked"}
        onOpenChange={(o) => {
          if (!o && !leaving.current) blocker.reset?.()
          leaving.current = false
        }}
        title={t("studio.agents.leave.title")}
        description={t("studio.agents.leave.description")}
        confirmLabel={t("studio.agents.leave.confirm")}
        cancelLabel={t("studio.agents.leave.stay")}
        destructive
        onConfirm={() => {
          leaving.current = true
          blocker.proceed?.()
        }}
      />
    </FormProvider>
  )
}

function TabCount({ n }: { n: number }) {
  if (!n) return null
  return (
    <span className="bg-muted text-muted-foreground min-w-4 rounded px-1 text-[0.7rem] leading-4 font-medium tabular-nums">
      {n}
    </span>
  )
}
